/**
 * THE PARTY CALL HISTORY PANEL — overnight v20 §D2.
 *
 * One customer's call log, newest first. This is the "history panel on the party" the
 * §5 legacy verdict names, and it is a COMPONENT rather than a page so the same rendering
 * can sit on any party surface a later phase builds. That is no longer hypothetical: it
 * is mounted BOTH on the party record (`PartyRecordPage`, the surface this header was
 * written waiting for) AND at its original routed home `parties/:partyId/calls`, which
 * stays for deep links.
 *
 * ===========================================================================
 * IT READS THE REGISTER ROUTE WITH `?party_id=`, AND THAT IS DELIBERATE
 * ===========================================================================
 * There is no party-scoped endpoint. The server exposes ONE list builder and the panel
 * is that builder with a filter — so the panel and the country register can never drift
 * in order, in permission, or in what they join. A second endpoint would be a second
 * implementation of the same read (the `listMine` lesson from the visits service).
 *
 * ===========================================================================
 * THREE STATES, AND THE THIRD ONE IS THE ONE THAT USUALLY GETS SKIPPED
 * ===========================================================================
 * Loading is skeleton rows, not a spinner. An error names the server's own sentence and
 * offers a retry. EMPTY says "no calls have been logged against this customer" — which
 * is a FACT about the customer, and must not be confused with the panel failing to load
 * or with the account not being allowed to see it. The permission refusal is a fourth,
 * separate state with its own words.
 *
 * NO MONEY, NO POSITION, NOTHING MASKED: `party_calls` carries neither (migration 56),
 * so nothing here branches on `'•••'`. The `note` is prose and renders in full — the
 * gate that protects it is `parties.view` at the server, and the same prose is ABSENT
 * from the events feed, which is the channel that cannot gate per caller.
 *
 * ===========================================================================
 * v24 C1 — THE PANEL GREW THE WRITE CONTROLS, GATED WHERE THE SERVER GATES
 * ===========================================================================
 * The log-call flow (with the serial field) and the per-entry amend control mount HERE,
 * because this panel is the one place every party surface already shares. Both render
 * ONLY for a `parties.create` holder in this country — the key that gates both routes
 * at the server — so a read-only seat sees exactly the read-only panel it always saw:
 * write controls are ABSENT without the permission, not disabled. Entries render the
 * serial (a CLAIM — AM-5) and the amends/amended-by links; the amend control is absent
 * on amendment rows because chains are flat and the screen does not offer what the
 * server refuses.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { useSession } from '../../auth/session';
import type { CountryCode } from '../../domain/countries';
import { Alert, Button, EmptyState, SkeletonRows } from '../../components/ui';
import { formatDateLong, formatTime } from '../inquiries/format';
import { AmendCallForm, LogCallForm } from './CallForms';
import { OutcomePill } from './OutcomePill';
import { fetchPartyCalls, partyCallErrorText, partyCallKeys } from './api';
import type { PartyCallFilters, PartyCallRow } from './api';

/** A panel is a panel: it shows the recent history, and the register holds the rest. */
export const PANEL_PER_PAGE = 20;

export function PartyCallHistoryPanel({
  countryCode,
  partyId,
  perPage = PANEL_PER_PAGE,
}: {
  countryCode: CountryCode;
  partyId: string;
  perPage?: number;
}): React.JSX.Element {
  const { can } = useSession();
  const canView = can(countryCode, 'parties.view');
  // The WRITE gate — the same `parties.create` both routes require at the server.
  const canWrite = can(countryCode, 'parties.create');
  const [logging, setLogging] = useState(false);
  const [amendingId, setAmendingId] = useState<string | null>(null);

  const filters: PartyCallFilters = { partyId, page: 1, perPage };
  const list = useQuery({
    queryKey: partyCallKeys.register(countryCode, filters),
    queryFn: ({ signal }) => fetchPartyCalls(countryCode, filters, signal),
    enabled: canView,
  });

  /* The log flow, shared by every state below that shows the panel at all: a customer
   * with no calls yet is exactly the customer someone is about to telephone. */
  const logFlow = !canWrite ? null : logging ? (
    <div className="rounded-md border border-line bg-surface p-3">
      <LogCallForm
        countryCode={countryCode}
        partyId={partyId}
        onDone={() => setLogging(false)}
      />
    </div>
  ) : (
    <div>
      <Button type="button" variant="ghost" onClick={() => setLogging(true)}>
        Log a call
      </Button>
    </div>
  );

  if (!canView) {
    return (
      <Alert tone="danger" title="Call history is not available to this account">
        Reading a customer&apos;s call log in {countryCode} needs the parties view
        permission. Ask an administrator to widen your access.
      </Alert>
    );
  }

  if (list.isPending) {
    return (
      <div className="rounded-md border border-line bg-surface px-4">
        <SkeletonRows rows={4} label="Loading the call history" />
      </div>
    );
  }

  if (list.isError) {
    return (
      <Alert tone="danger" title="The call history could not be loaded">
        <p>{partyCallErrorText(list.error)}</p>
        <button
          type="button"
          onClick={() => {
            void list.refetch();
          }}
          className="mt-1 font-medium underline underline-offset-2"
        >
          Try again.
        </button>
      </Alert>
    );
  }

  const rows = list.data.data;
  if (rows.length === 0) {
    return (
      <div className="flex flex-col gap-2" data-testid="party-call-history">
        {logFlow}
        <div className="rounded-md border border-dashed border-line bg-surface">
          {/* A fact about the CUSTOMER — not a failure, and not a permission refusal. */}
          <EmptyState title="No calls have been logged against this customer yet." />
        </div>
      </div>
    );
  }

  const total = list.data.total;
  return (
    <div className="flex flex-col gap-2" data-testid="party-call-history">
      {logFlow}
      <ol className="flex flex-col">
        {rows.map((row) => (
          <PartyCallEntry
            key={row.id}
            row={row}
            canWrite={canWrite}
            amending={amendingId === row.id}
            onToggleAmend={() => setAmendingId(amendingId === row.id ? null : row.id)}
          />
        ))}
      </ol>
      {total > rows.length ? (
        <p className="text-body13 text-fg-secondary">
          Showing the {rows.length} most recent of {total} logged calls.
        </p>
      ) : null}
    </div>
  );
}

/**
 * One entry. The ORDER of the line is the reading order a call log wants: WHAT came of
 * it, WHEN, WHO — then the prose. The timestamp carries its ISO instant in `title` so a
 * reader can see the exact server time without the row shouting it.
 *
 * v24 C1 additions, each with its rule:
 *   · the SERIAL renders labelled as what it is — a claim (AM-5), so the label is
 *     "Serial (as stated)", never bare digits a reader might take for a verified key;
 *   · "Amends call N" / "Amended by call N" are same-page anchors (`#party-call-N`) —
 *     both halves of a correction pair are entries of this same list, and a link that
 *     left the page would imply a call has a page of its own (it does not);
 *   · the AMEND control renders only for a write seat and only on rows that are not
 *     themselves amendments — chains are flat, and the screen does not offer what the
 *     server refuses.
 */
export function PartyCallEntry({
  row,
  canWrite = false,
  amending = false,
  onToggleAmend,
}: {
  row: PartyCallRow;
  canWrite?: boolean;
  amending?: boolean;
  onToggleAmend?: () => void;
}): React.JSX.Element {
  const isAmendment = row.amends_call_id !== null;
  return (
    <li
      id={`party-call-${row.id}`}
      className="flex flex-col gap-1 border-b border-line py-3 last:border-b-0"
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <OutcomePill outcome={row.outcome} />
        <span className="tnum text-body13 text-fg" title={row.logged_at}>
          {formatDateLong(row.logged_at)} · {formatTime(row.logged_at)}
        </span>
        <span className="text-body13 text-fg-secondary">
          {/* NOT NULL in the column, so a missing name is a join that was not made,
              never an anonymous call. Say which, rather than printing an em dash. */}
          {row.logged_by_name ?? `User ${row.logged_by}`}
        </span>
        {canWrite && !isAmendment && onToggleAmend !== undefined ? (
          <button
            type="button"
            onClick={onToggleAmend}
            className="text-body13 font-medium text-indigo hover:underline"
          >
            {amending ? 'Close the correction' : 'Amend'}
          </button>
        ) : null}
      </div>
      {isAmendment || row.tally_serial !== null || row.amended_by_call_ids.length > 0 ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-body13 text-fg-secondary">
          {isAmendment ? (
            <a
              href={`#party-call-${String(row.amends_call_id)}`}
              className="font-medium text-indigo hover:underline"
            >
              Amends call {row.amends_call_id}
            </a>
          ) : null}
          {row.amended_by_call_ids.map((id) => (
            <a
              key={id}
              href={`#party-call-${id}`}
              className="font-medium text-indigo hover:underline"
            >
              Amended by call {id}
            </a>
          ))}
          {row.tally_serial === null ? null : (
            /* A CLAIM, not a fact (AM-5) — the label says so. */
            <span className="tnum">Serial (as stated): {row.tally_serial}</span>
          )}
        </div>
      ) : null}
      <p className="whitespace-pre-wrap text-body13 text-fg">{row.note}</p>
      {amending ? <AmendCallForm row={row} onDone={() => onToggleAmend?.()} /> : null}
    </li>
  );
}
